/**
 * 『ヰ世界の宝石譚』 (Isekai no Housekitan / Tales of Gems in Isekai)
 * 作詞・作曲・編曲：sasakure.UK / MV映像：駿 / タイポグラフィ：本忠学 / イラスト：ヰ世界情緒
 * Official MV: https://youtu.be/hcqXjcO791s (#13)
 *
 * ジェネラティブアート・コンセプト（1シーン完結・歌詞表示なし・「瓦礫×宝石×崩壊×デジタルグリッチ」のド派手カオス演出）:
 * - MV全編を貫く「美しく煌めくブリリアントカットの宝石群」と「崩壊した世界の瓦礫（コンクリート塊・露出した鉄筋・折れた鉄骨トラス・黒い亀裂モノリス・砕けた歯車）」が
 *   無重力と暴風の中で激しく渦巻き、衝突・破砕し合うカオスな空間を表現。
 * - 「🎲 転生（再生成）」ボタンを押すたびに、MVの4つの劇的な「瓦礫×宝石」カオスモードが切り替わる：
 *   0. Mode 0 [深蒼の暴風と瓦礫・宝石の激突 (Abyssal Maelstrom of Rubble & Gems)]:
 *      深海の蒼碧〜紅蓮グラデーション空間で、無数のコンクリート瓦礫・鉄骨・黒い亀裂モノリスとブリリアントカット宝石が激しく渦巻き砕け散る。
 *   1. Mode 1 [劫火の崩壊都市と赤熱瓦礫・紅玉の嵐 (Scorched Ruin & Ruby Cataclysm)]:
 *      燃え盛る琥珀と紅蓮の爆炎の中、赤熱した亀裂を持つ瓦礫群・傾いた鉄骨廃墟と、ルビー＆蒼碧の宝石が連鎖破砕する。
 *   2. Mode 2 [白霧の無重力大崩壊と閃光クラック (Zero-G Rubble Cataclysm & Crystal Fracture)]:
 *      白霧と濃紺の無重力空間で、巨大な瓦礫塊・黒い立方体モノリス・鉄骨・歯車・アクアマリン宝石が猛烈な上昇気流で乱舞し、青白い結晶稲妻が走る。
 *   3. Mode 3 [天頂の後光とプリズム結晶樹・大破砕渦 (Zenith Halo & Prismatic Shatter Vortex)]:
 *      天頂からのゴッドレイと分光プリズム虹彩の中、暴走する結晶樹脈とともに瓦礫と極彩色の宝石群が螺旋状に吹き荒れる。
 */

export class HousekiGenerator {
  constructor(p) {
    this.p = p;
    this.time = 0;
    this.modeIndex = -1; // 初回initで決定、再生成で4つのMV崩壊・宝石カオスモードを切り替え

    // 3Dブリリアントカット宝石群（高速回転・多層）
    this.gems = [];
    // 吹き飛ぶ瓦礫（多角形コンクリート塊・露出鉄筋・黒い亀裂モノリス・鉄骨トラス・破損歯車）
    this.debrisMonoliths = [];
    // 画面を引き裂く結晶クラック稲妻（houseki_sb4.jpg Row 0 Col 3）
    this.crystalLightnings = [];
    // デジタル・ピクセルグリッチ＆RGB色収差ティアリング帯
    this.glitchBlocks = [];
    // 立ち昇る火の粉・粉塵・結晶スパーク
    this.emberSparks = [];
    // 天頂のゴッドレイ
    this.godRays = [];
    // 暴走する結晶樹脈・崩落都市の鉄骨廃墟シルエット
    this.crystalBranches = [];
    this.ruinSilhouettes = [];
    // カーソル移動軌跡の結晶スパーク
    this.cursorTrail = [];

    this.pointerX = 0;
    this.pointerY = 0;
    this.parallaxX = 0;
    this.parallaxY = 0;
    this.impactShake = 0;
    this.flareBoost = 0;
  }

  onReroll() {
    this.modeIndex = (this.modeIndex + 1) % 4;
    this.impactShake = 12;
    this.flareBoost = 1.6;
  }

  init(params) {
    const p = this.p;
    const w = p.width || 800;
    const h = p.height || 600;
    const density = params ? params.density : 0.5;
    const scale = Math.min(w, h) / 720;

    if (this.modeIndex < 0) {
      this.modeIndex = Math.floor(p.random(4));
    }

    this.pointerX = w * 0.5;
    this.pointerY = h * 0.5;
    this.gems = [];
    this.debrisMonoliths = [];
    this.crystalLightnings = [];
    this.glitchBlocks = [];
    this.emberSparks = [];
    this.godRays = [];
    this.crystalBranches = [];
    this.ruinSilhouettes = [];
    this.cursorTrail = [];

    // 1. 乱舞する3Dブリリアントカット宝石群（高密度・遠近3レイヤー）
    const gemCount = Math.floor(44 + density * 52);
    for (let i = 0; i < gemCount; i++) {
      const layer = i < 9 ? 2 : i < 28 ? 1 : 0;
      const baseSize =
        layer === 2
          ? p.random(54, 92) * scale
          : layer === 1
          ? p.random(24, 48) * scale
          : p.random(10, 22) * scale;

      const angle = p.random(p.TWO_PI);
      const speed = p.random(0.8, 3.0) * (layer === 2 ? 1.35 : 1.0);
      this.gems.push({
        x: p.random(w),
        y: p.random(h),
        vx: Math.cos(angle) * speed * 0.85,
        vy: -Math.abs(Math.sin(angle) * speed) - p.random(0.4, 1.4),
        size: baseSize,
        rot: p.random(p.TWO_PI),
        rotSpeed: p.random(-0.055, 0.055),
        tilt: p.random(p.TWO_PI),
        tiltSpeed: p.random(0.035, 0.09),
        hueVariant: i % 5, // 0: フォスフォフィライト蒼碧, 1: アイスシアン, 2: サファイア, 3: 紅蓮ルビー, 4: 琥珀アンバー
        layer,
        depth: layer === 2 ? p.random(1.3, 1.9) : layer === 1 ? p.random(0.7, 1.25) : p.random(0.3, 0.65),
        sparklePhase: p.random(p.TWO_PI),
        sparkleSpeed: p.random(2.8, 7.0),
        orbitPhase: p.random(p.TWO_PI)
      });
    }

    // 2. 吹き荒れる大量の「瓦礫（がれき）」群：
    //    - 'rock_rubble': 砕けた多角形コンクリート塊・岩塊（鉄筋の突き出し＆内部の結晶/マグマ亀裂つき）
    //    - 'monolith': 光る亀裂を宿した黒い3D立方体モノリス（houseki_maxres1.jpg）
    //    - 'girder': 折れ曲がった鉄骨H鋼・トラス構造体（houseki_maxres2.jpg）
    //    - 'gear_ring': 砕けた巨大歯車パーツ（houseki_maxres2.jpg）
    const debrisCount = Math.floor(32 + density * 36);
    const shapeTypes = ['rock_rubble', 'rock_rubble', 'monolith', 'girder', 'gear_ring'];
    for (let i = 0; i < debrisCount; i++) {
      const shapeType = shapeTypes[i % shapeTypes.length];
      const polyVerts = [];
      const vCount = Math.floor(p.random(5, 8));
      for (let v = 0; v < vCount; v++) {
        const a = (p.TWO_PI / vCount) * v + p.random(-0.22, 0.22);
        const r = p.random(0.38, 0.68);
        polyVerts.push({ a, r });
      }

      this.debrisMonoliths.push({
        x: p.random(w),
        y: p.random(h),
        vx: p.random(-1.6, 1.6) * scale,
        vy: p.random(-2.2, 1.1) * scale,
        size: p.random(26, 96) * scale,
        rot: p.random(p.TWO_PI),
        rotSpeed: p.random(-0.042, 0.042),
        tilt: p.random(0.25, 0.95),
        shapeType,
        polyVerts,
        hasRebar: i % 2 === 0, // コンクリート瓦礫から突き出た鉄筋
        hasCoreSlit: i % 3 !== 0, // 瓦礫内部で発光する宝石脈／マグマ亀裂
        slitPhase: p.random(p.TWO_PI),
        depth: p.random(0.35, 1.65)
      });
    }

    // 3. 舞い上がる粉塵・火の粉・宝石ダスト粒子
    const sparkCount = Math.floor(85 + density * 95);
    for (let i = 0; i < sparkCount; i++) {
      this.emberSparks.push({
        x: p.random(w),
        y: p.random(h),
        vx: p.random(-2.2, 2.2) * scale,
        vy: p.random(-3.6, -0.7) * scale,
        size: p.random(2.2, 7.5) * scale,
        isRubbleChip: i % 3 === 0, // 細かい瓦礫の破片チップ
        isFire: i % 2 === 0,
        rot: p.random(p.TWO_PI),
        rotSpeed: p.random(-0.08, 0.08),
        alpha: p.random(130, 255),
        phase: p.random(p.TWO_PI)
      });
    }

    // 4. デジタル・ピクセルグリッチ＆RGB色収差ブロック群（houseki_maxresdefault.jpg）
    const glitchCount = Math.floor(55 + density * 60);
    const glitchPalette = [
      [38, 242, 208],  // エメラルドティール
      [56, 185, 255],  // サイバーブルー
      [255, 62, 92],   // クリムゾンレッド
      [255, 168, 38],  // アンバーオレンジ
      [240, 252, 255]  // ホワイトデータ
    ];
    for (let i = 0; i < glitchCount; i++) {
      this.glitchBlocks.push({
        x: p.random(w),
        y: p.random(h),
        w: p.random(4, 38) * scale,
        h: p.random(2.5, 9) * scale,
        col: glitchPalette[i % glitchPalette.length],
        blinkSpeed: p.random(4.0, 12.0),
        phase: p.random(p.TWO_PI),
        depth: p.random(0.3, 1.3)
      });
    }

    // 5. 天頂のゴッドレイ
    const rayCount = 9;
    for (let i = 0; i < rayCount; i++) {
      this.godRays.push({
        angle: p.map(i, 0, rayCount - 1, -0.62, 0.62) + p.random(-0.04, 0.04),
        topWidth: p.random(16, 46) * scale,
        bottomWidth: p.random(95, 230) * scale,
        alpha: p.random(24, 58),
        swaySpeed: p.random(0.6, 1.4),
        phase: p.random(p.TWO_PI)
      });
    }

    // 6. 暴走する結晶樹脈と崩壊都市の瓦礫・鉄骨シルエット
    const branchCount = Math.floor(18 + density * 14);
    for (let i = 0; i < branchCount; i++) {
      const spread = p.map(i, 0, branchCount - 1, -w * 0.45, w * 0.45);
      this.crystalBranches.push({
        baseX: w * 0.5 + spread * 0.5,
        topX: w * 0.5 + spread * 1.25,
        heightRatio: p.random(0.42, 0.88),
        curve: p.random(-65, 65) * scale,
        phase: p.random(p.TWO_PI)
      });
    }

    // 地平線の崩壊したビル・鉄骨トラス・傾いた電柱シルエット
    const ruinCount = 16;
    for (let i = 0; i < ruinCount; i++) {
      this.ruinSilhouettes.push({
        xRatio: p.map(i, 0, ruinCount - 1, 0.03, 0.97) + p.random(-0.02, 0.02),
        w: p.random(14, 48) * scale,
        h: p.random(65, 220) * scale,
        tilt: p.random(-0.42, 0.42),
        isPylon: i % 3 === 0,
        isTruss: i % 2 === 1
      });
    }

    // 初回に結晶稲妻を1本走らせる
    this.spawnCrystalLightning(w * 0.5, h * 0.45, w, h, scale);
  }

  spawnCrystalLightning(x, y, w, h, scale) {
    const p = this.p;
    const branches = [];
    const rayCount = Math.floor(p.random(4, 8));
    for (let r = 0; r < rayCount; r++) {
      const baseAng = (p.TWO_PI / rayCount) * r + p.random(-0.3, 0.3);
      const pts = [{ x, y }];
      let cx = x;
      let cy = y;
      const segs = 7;
      const segLen = p.random(35, 75) * scale;
      for (let s = 1; s <= segs; s++) {
        const a = baseAng + p.random(-0.45, 0.45);
        cx += Math.cos(a) * segLen;
        cy += Math.sin(a) * segLen;
        pts.push({ x: cx, y: cy });
      }
      branches.push(pts);
    }

    this.crystalLightnings.push({
      branches,
      alpha: 255,
      isCrimson: p.random() < 0.35
    });
    if (this.crystalLightnings.length > 4) {
      this.crystalLightnings.shift();
    }
  }

  onPointerDown(x, y) {
    const p = this.p;
    const w = p.width;
    const h = p.height;
    const scale = Math.min(w, h) / 720;

    this.impactShake = 14;
    this.flareBoost = 2.0;

    // クリック地点に結晶クラック稲妻を走らせる
    this.spawnCrystalLightning(x, y, w, h, scale);
  }

  onPointerMove(x, y) {
    const p = this.p;
    const scale = Math.min(p.width, p.height) / 720;
    this.pointerX = x;
    this.pointerY = y;

    this.cursorTrail.push({
      x: x + p.random(-10, 10) * scale,
      y: y + p.random(-10, 10) * scale,
      size: p.random(8, 18) * scale,
      rot: p.random(p.TWO_PI),
      hueVariant: p.frameCount % 5,
      alpha: 245
    });
    if (this.cursorTrail.length > 34) {
      this.cursorTrail.shift();
    }
  }

  draw(params) {
    const p = this.p;
    const w = p.width;
    const h = p.height;
    const motion = params ? params.motion : 0.5;
    const glow = params ? params.glow : 0.6;
    const scale = Math.min(w, h) / 720;

    const dt = Math.min(p.deltaTime || 16.67, 50) / 16.67;
    const speedMult = 0.55 + motion * 1.35;
    this.time += 0.018 * dt * speedMult;
    this.flareBoost = p.lerp(this.flareBoost, 0, 0.07 * dt);
    this.impactShake = p.lerp(this.impactShake, 0, 0.12 * dt);

    // 周期的に空間に走る結晶クラック稲妻
    if (p.frameCount % Math.max(36, Math.floor(75 - motion * 35)) === 0) {
      const lx = p.random(w * 0.18, w * 0.82);
      const ly = p.random(h * 0.2, h * 0.75);
      this.spawnCrystalLightning(lx, ly, w, h, scale);
    }

    // 視差（パララックス）＋微細な乱気流シェイク
    const targetPx = ((this.pointerX || w * 0.5) / w - 0.5) * 36;
    const targetPy = ((this.pointerY || h * 0.5) / h - 0.5) * 36;
    this.parallaxX = p.lerp(this.parallaxX, targetPx, 0.08);
    this.parallaxY = p.lerp(this.parallaxY, targetPy, 0.08);

    const shakeX = (p.random(-1, 1) * this.impactShake + Math.sin(this.time * 14) * 1.2 * motion) * scale;
    const shakeY = (p.random(-1, 1) * this.impactShake + Math.cos(this.time * 12) * 1.2 * motion) * scale;

    p.push();
    p.translate(shakeX, shakeY);

    const cx = w * 0.5 - this.parallaxX * 0.45;
    const cy = h * 0.5 - this.parallaxY * 0.45;

    // =========================================================================
    // 再生成（Reroll）で切り替わる4つの「瓦礫×宝石」カオス崩壊モード
    // =========================================================================
    if (this.modeIndex === 0) {
      this.drawMode0AbyssalRubbleGemStorm(cx, cy, w, h, scale, dt, speedMult, glow);
    } else if (this.modeIndex === 1) {
      this.drawMode1ScorchedRuinCataclysm(cx, cy, w, h, scale, dt, speedMult, glow);
    } else if (this.modeIndex === 2) {
      this.drawMode2ZeroGCataclysm(cx, cy, w, h, scale, dt, speedMult, glow);
    } else {
      this.drawMode3PrismaticShatterVortex(cx, cy, w, h, scale, dt, speedMult, glow);
    }

    // =========================================================================
    // 共通カオスレイヤー：結晶クラック稲妻・瓦礫チップ＆火の粉・グリッチ
    // =========================================================================
    this.updateAndDrawCrystalLightnings(scale, dt, glow);
    this.updateAndDrawEmberSparks(w, h, scale, dt, speedMult, glow);
    this.drawDigitalGlitchAndChromaticTear(w, h, scale, glow);
    this.updateAndDrawCursorTrail(scale, dt, glow);

    p.pop();

    // シネマスコープ上下黒帯
    this.drawCinemascopeLetterbox(w, h);
  }

  /**
   * Mode 0: 深蒼の暴風と瓦礫・宝石の激突（houseki_maxresdefault.jpg / houseki_maxres2.jpg）
   */
  drawMode0AbyssalRubbleGemStorm(cx, cy, w, h, scale, dt, speedMult, glow) {
    const p = this.p;
    const ctx = p.drawingContext;

    // 1. 深淵のブルーブラック背景と左下の紅蓮の照り返し・右下の蒼碧グロー
    p.background(4, 9, 16);
    ctx.save();
    const bgGrad = ctx.createLinearGradient(0, h * 0.1, w, h * 0.95);
    bgGrad.addColorStop(0, 'rgba(6, 16, 28, 0.2)');
    bgGrad.addColorStop(0.5, `rgba(16, 68, 96, ${0.55 * glow})`);
    bgGrad.addColorStop(0.82, `rgba(38, 185, 205, ${0.65 * glow})`);
    bgGrad.addColorStop(1, `rgba(235, 65, 48, ${0.52 * glow})`);
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();

    // 2. 天頂ゴッドレイと崩壊都市の瓦礫・鉄骨シルエット
    this.drawVolumetricGodRays(w, h, scale, glow * 0.75, [120, 230, 255]);
    this.drawCollapsedRuinsHorizon(w, h, scale, false, glow);

    // 3. 吹き荒れる多角形瓦礫・鉄骨・黒きモノリスと乱舞するブリリアントカット宝石群
    this.updateAndDrawDebrisMonoliths(cx, cy, w, h, scale, dt, speedMult, false, glow);
    this.updateAndDrawBrilliantGems(cx, cy, w, h, scale, dt, speedMult, glow, false);
  }

  /**
   * Mode 1: 劫火の崩壊都市と赤熱瓦礫・紅玉の嵐（houseki_sb1.jpg Row 3-4）
   */
  drawMode1ScorchedRuinCataclysm(cx, cy, w, h, scale, dt, speedMult, glow) {
    const p = this.p;
    const ctx = p.drawingContext;

    // 1. 燃え盛る琥珀・紅蓮の巨大火球と黒煙の空
    p.background(16, 5, 4);
    ctx.save();
    const fireGrad = ctx.createRadialGradient(
      cx + w * 0.06,
      cy - h * 0.04,
      15 * scale,
      cx,
      cy,
      Math.max(w, h) * 0.75
    );
    fireGrad.addColorStop(0, `rgba(255, 250, 210, ${0.98 * glow})`);
    fireGrad.addColorStop(0.25, `rgba(255, 162, 38, ${0.92 * glow})`);
    fireGrad.addColorStop(0.55, `rgba(218, 38, 18, ${0.78 * glow})`);
    fireGrad.addColorStop(0.85, 'rgba(48, 10, 12, 0.92)');
    fireGrad.addColorStop(1, 'rgba(10, 3, 4, 0.98)');
    ctx.fillStyle = fireGrad;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();

    // 2. 吹き上がる黒煙・火焔雲のうねり
    p.push();
    p.noStroke();
    for (let i = 0; i < 9; i++) {
      const sx = ((i * 0.13 + this.time * 0.03) % 1.1 - 0.05) * w;
      const sy = h * (0.25 + (i % 3) * 0.22) + Math.sin(this.time * 1.4 + i) * 24 * scale;
      p.fill(i % 2 === 0 ? [255, 120, 28, 55 * glow] : [22, 8, 8, 110]);
      p.circle(sx, sy, (140 + (i % 4) * 55) * scale);
    }
    p.pop();

    // 3. 崩落都市の鉄骨・瓦礫山のシルエット
    this.drawCollapsedRuinsHorizon(w, h, scale, true, glow);

    // 4. マグマ亀裂を宿した瓦礫塊・黒いモノリスと紅蒼のブリリアントカット宝石群
    this.updateAndDrawDebrisMonoliths(cx, cy, w, h, scale, dt, speedMult, true, glow);
    this.updateAndDrawBrilliantGems(cx, cy, w, h, scale, dt, speedMult, glow, true);
  }

  /**
   * Mode 2: 白霧の無重力大崩壊と閃光クラック（houseki_maxres1.jpg / houseki_sb4.jpg Row 0 Col 3 完全再現）
   */
  drawMode2ZeroGCataclysm(cx, cy, w, h, scale, dt, speedMult, glow) {
    const p = this.p;
    const ctx = p.drawingContext;

    // 1. 眩い白霧から深淵の群青へなだれ込む斜めグラデーション（houseki_maxres1.jpg）
    p.background(20, 26, 36);
    ctx.save();
    const fogGrad = ctx.createLinearGradient(0, 0, w, h);
    fogGrad.addColorStop(0, 'rgba(248, 250, 255, 0.96)');
    fogGrad.addColorStop(0.38, 'rgba(180, 205, 225, 0.82)');
    fogGrad.addColorStop(0.72, 'rgba(38, 58, 82, 0.65)');
    fogGrad.addColorStop(1, 'rgba(10, 14, 22, 0.95)');
    ctx.fillStyle = fogGrad;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();

    // 2. 暴走する結晶樹脈と無重力瓦礫・モノリス・鉄骨・歯車・宝石の大渦
    this.drawGlowingCrystalTreeVeins(cx, cy, w, h, scale, glow);
    this.updateAndDrawDebrisMonoliths(cx, cy, w, h, scale, dt, speedMult, false, glow);
    this.updateAndDrawBrilliantGems(cx, cy, w, h, scale, dt, speedMult, glow, false);
  }

  /**
   * Mode 3: 天頂の後光とプリズム結晶樹・大破砕渦（houseki_maxres2.jpg / houseki_sb2.jpg / houseki_sb4.jpg）
   */
  drawMode3PrismaticShatterVortex(cx, cy, w, h, scale, dt, speedMult, glow) {
    const p = this.p;
    const ctx = p.drawingContext;

    // 1. 紫〜蒼碧〜深紅が交錯するカオスプリズム夜空（houseki_sb2.jpg Row 0-1）
    p.background(8, 6, 18);
    ctx.save();
    const prismGrad = ctx.createRadialGradient(cx, cy, 20 * scale, cx, cy, Math.max(w, h) * 0.75);
    prismGrad.addColorStop(0, `rgba(165, 85, 255, ${0.48 * glow})`);
    prismGrad.addColorStop(0.45, `rgba(28, 165, 225, ${0.42 * glow})`);
    prismGrad.addColorStop(0.78, `rgba(215, 28, 85, ${0.35 * glow})`);
    prismGrad.addColorStop(1, 'rgba(5, 6, 12, 0.95)');
    ctx.fillStyle = prismGrad;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();

    // 2. 天頂からの強烈なゴッドレイと分光プリズム虹彩アーク
    this.drawVolumetricGodRays(w, h, scale, glow * 1.35, [220, 240, 255]);

    ctx.save();
    const rainbowR = Math.max(w, h) * 0.56;
    const rainbowColors = [
      'rgba(255, 65, 95, 0.24)',
      'rgba(255, 195, 55, 0.22)',
      'rgba(55, 250, 185, 0.24)',
      'rgba(55, 185, 255, 0.28)',
      'rgba(175, 75, 255, 0.24)'
    ];
    for (let i = 0; i < rainbowColors.length; i++) {
      ctx.strokeStyle = rainbowColors[i];
      ctx.lineWidth = 15 * scale;
      ctx.beginPath();
      ctx.arc(cx - w * 0.14, cy + h * 0.22, rainbowR - i * 14 * scale, -p.HALF_PI * 0.95, 0.35);
      ctx.stroke();
    }
    ctx.restore();

    // 3. 暴走する青白い結晶樹脈・崩壊瓦礫シルエット・モノリス・ブリリアントカット宝石群
    this.drawGlowingCrystalTreeVeins(cx, cy, w, h, scale, glow);
    this.drawCollapsedRuinsHorizon(w, h, scale, false, glow);
    this.updateAndDrawDebrisMonoliths(cx, cy, w, h, scale, dt, speedMult, false, glow);
    this.updateAndDrawBrilliantGems(cx, cy, w, h, scale, dt, speedMult, glow, false);
  }

  /**
   * 崩壊都市の瓦礫の山・傾いた鉄骨トラス・折れた電柱シルエット（houseki_sb2.jpg Row 2 Col 5 / houseki_sb3.jpg Row 0 Col 3）
   */
  drawCollapsedRuinsHorizon(w, h, scale, isFireMode, glow) {
    const p = this.p;
    const baseY = h * 0.93;

    p.push();
    // 1. 地平線に積み重なる崩落コンクリート瓦礫の山
    p.noStroke();
    p.fill(isFireMode ? [16, 5, 5, 242] : [7, 11, 17, 238]);
    p.beginShape();
    p.vertex(0, h);
    p.vertex(0, baseY - 25 * scale);
    p.vertex(w * 0.18, baseY - 68 * scale);
    p.vertex(w * 0.34, baseY - 18 * scale);
    p.vertex(w * 0.52, baseY - 75 * scale);
    p.vertex(w * 0.71, baseY - 22 * scale);
    p.vertex(w * 0.88, baseY - 64 * scale);
    p.vertex(w, baseY - 28 * scale);
    p.vertex(w, h);
    p.endShape(p.CLOSE);

    // 2. 傾いた鉄骨トラス・崩落ビル柱・折れた電柱シルエット群
    for (let i = 0; i < this.ruinSilhouettes.length; i++) {
      const rn = this.ruinSilhouettes[i];
      const rx = rn.xRatio * w - this.parallaxX * 0.35;
      p.push();
      p.translate(rx, baseY);
      p.rotate(rn.tilt);

      if (rn.isTruss) {
        // むき出しの鉄骨トラスフレーム
        p.noFill();
        p.stroke(isFireMode ? [28, 9, 9, 235] : [12, 18, 26, 225]);
        p.strokeWeight(3.2 * scale);
        p.rect(-rn.w * 0.5, -rn.h, rn.w, rn.h);
        p.strokeWeight(1.8 * scale);
        for (let y = -rn.h; y < 0; y += rn.w) {
          p.line(-rn.w * 0.5, y, rn.w * 0.5, Math.min(0, y + rn.w));
          p.line(rn.w * 0.5, y, -rn.w * 0.5, Math.min(0, y + rn.w));
        }
      } else {
        // 崩落したコンクリート柱と突き出た鉄筋
        p.noStroke();
        p.fill(isFireMode ? [20, 7, 7, 238] : [9, 14, 20, 230]);
        p.rect(-rn.w * 0.5, -rn.h, rn.w, rn.h);
        p.stroke(isFireMode ? [45, 14, 14, 220] : [20, 30, 42, 210]);
        p.strokeWeight(1.8 * scale);
        p.line(-rn.w * 0.25, -rn.h, -rn.w * 0.35, -rn.h - 26 * scale);
        p.line(rn.w * 0.2, -rn.h, rn.w * 0.35, -rn.h - 19 * scale);
      }

      if (rn.isPylon) {
        // 電柱・鉄塔の横腕木
        p.stroke(isFireMode ? [32, 10, 10, 225] : [15, 22, 32, 215]);
        p.strokeWeight(2.5 * scale);
        p.line(-rn.w * 1.6, -rn.h * 0.85, rn.w * 1.6, -rn.h * 0.85);
        p.line(-rn.w * 1.2, -rn.h * 0.68, rn.w * 1.2, -rn.h * 0.68);
      }
      p.pop();
    }
    p.pop();
  }

  /**
   * 画面を引き裂く「青白い／深紅の結晶クラック稲妻」（houseki_sb4.jpg Row 0 Col 3）
   */
  updateAndDrawCrystalLightnings(scale, dt, glow) {
    const p = this.p;
    p.push();
    p.noFill();

    for (let i = this.crystalLightnings.length - 1; i >= 0; i--) {
      const cl = this.crystalLightnings[i];
      cl.alpha -= 11.0 * dt;
      if (cl.alpha <= 0) {
        this.crystalLightnings.splice(i, 1);
        continue;
      }

      for (let b = 0; b < cl.branches.length; b++) {
        const pts = cl.branches[b];
        // 外側のシアン／紅蓮ハロー
        p.stroke(
          cl.isCrimson ? 255 : 48,
          cl.isCrimson ? 45 : 235,
          cl.isCrimson ? 75 : 255,
          cl.alpha * 0.65 * glow
        );
        p.strokeWeight(6.5 * scale);
        p.beginShape();
        for (let k = 0; k < pts.length; k++) {
          p.vertex(pts[k].x, pts[k].y);
        }
        p.endShape();

        // 中心の純白クラック閃光
        p.stroke(255, 255, 255, cl.alpha);
        p.strokeWeight(2.0 * scale);
        p.beginShape();
        for (let k = 0; k < pts.length; k++) {
          p.vertex(pts[k].x, pts[k].y);
        }
        p.endShape();
      }
    }

    p.pop();
  }

  /**
   * 天頂から降り注ぐボリューメトリック・ゴッドレイ（houseki_maxres2.jpg）
   */
  drawVolumetricGodRays(w, h, scale, intensity, rgb) {
    const p = this.p;
    const topX = w * 0.45 - this.parallaxX * 0.25;
    const topY = h * 0.04;

    p.push();
    p.noStroke();
    for (let i = 0; i < this.godRays.length; i++) {
      const ray = this.godRays[i];
      const sway = Math.sin(this.time * ray.swaySpeed + ray.phase) * 0.05;
      const a = ray.angle + sway;
      const bottomX = topX + Math.tan(a) * h * 0.96;

      p.fill(rgb[0], rgb[1], rgb[2], ray.alpha * intensity);
      p.quad(
        topX - ray.topWidth * 0.5,
        topY,
        topX + ray.topWidth * 0.5,
        topY,
        bottomX + ray.bottomWidth * 0.5,
        h,
        bottomX - ray.bottomWidth * 0.5,
        h
      );
    }
    p.pop();
  }

  /**
   * 吹き荒れる「瓦礫（多角形コンクリート塊・露出鉄筋・黒い亀裂モノリス・鉄骨Iビーム・破損歯車）」の更新と描画
   */
  updateAndDrawDebrisMonoliths(cx, cy, w, h, scale, dt, speedMult, isFireMode, glow) {
    const p = this.p;

    for (let i = 0; i < this.debrisMonoliths.length; i++) {
      const mc = this.debrisMonoliths[i];
      // 暴風と渦によるダイナミックな移動
      const swirlX = Math.sin(this.time * 1.5 + mc.slitPhase) * 0.55 * scale;
      const swirlY = Math.cos(this.time * 1.3 + mc.slitPhase) * 0.45 * scale;
      mc.x += (mc.vx + swirlX) * speedMult * dt;
      mc.y += (mc.vy + swirlY) * speedMult * dt;
      mc.rot += mc.rotSpeed * speedMult * dt;

      if (mc.x < -110 * scale) mc.x = w + 100 * scale;
      if (mc.x > w + 110 * scale) mc.x = -100 * scale;
      if (mc.y < -110 * scale) mc.y = h + 100 * scale;
      if (mc.y > h + 110 * scale) mc.y = -100 * scale;

      const x = mc.x - this.parallaxX * mc.depth;
      const y = mc.y - this.parallaxY * mc.depth;
      const s = mc.size;

      p.push();
      p.translate(x, y);
      p.rotate(mc.rot);

      if (mc.shapeType === 'rock_rubble') {
        // 砕けた多角形コンクリート・岩塊の瓦礫（露出した鉄筋＆内部の宝石脈・マグマ亀裂つき）
        if (mc.hasRebar) {
          p.stroke(isFireMode ? [95, 42, 32, 230] : [65, 82, 98, 220]);
          p.strokeWeight(2.2 * scale);
          p.line(-s * 0.72, -s * 0.18, s * 0.78, s * 0.22);
          p.line(-s * 0.22, -s * 0.75, s * 0.15, s * 0.68);
        }

        // 瓦礫の立体シャドウ面
        p.noStroke();
        p.fill(isFireMode ? [18, 6, 6, 245] : [10, 14, 22, 242]);
        p.beginShape();
        for (let v = 0; v < mc.polyVerts.length; v++) {
          const pt = mc.polyVerts[v];
          p.vertex(Math.cos(pt.a) * s * pt.r + 6 * scale, Math.sin(pt.a) * s * pt.r + 6 * scale);
        }
        p.endShape(p.CLOSE);

        // 瓦礫のメイン破断面
        p.fill(isFireMode ? [34, 12, 12, 245] : [22, 30, 42, 242]);
        p.stroke(isFireMode ? [255, 128, 42, 175] : [85, 205, 235, 165]);
        p.strokeWeight(1.2 * scale);
        p.beginShape();
        for (let v = 0; v < mc.polyVerts.length; v++) {
          const pt = mc.polyVerts[v];
          p.vertex(Math.cos(pt.a) * s * pt.r, Math.sin(pt.a) * s * pt.r);
        }
        p.endShape(p.CLOSE);

        // 瓦礫内部を走る発光クラック（宝石の結晶脈または赤熱亀裂）
        if (mc.hasCoreSlit) {
          p.stroke(isFireMode ? [255, 165, 55, 235] : [65, 245, 235, 235]);
          p.strokeWeight(2.4 * scale);
          p.line(-s * 0.28, -s * 0.12, 0, s * 0.08);
          p.line(0, s * 0.08, s * 0.26, -s * 0.18);
        }
      } else if (mc.shapeType === 'girder') {
        // 吹き飛ぶ鉄骨H鋼・トラス構造体の残骸（houseki_maxres2.jpg 右下・左上の鉄骨）
        p.fill(isFireMode ? [28, 10, 10, 242] : [16, 22, 32, 238]);
        p.stroke(isFireMode ? [255, 130, 45, 185] : [155, 215, 245, 170]);
        p.strokeWeight(1.2 * scale);
        p.rectMode(p.CENTER);
        p.rect(0, 0, s * 1.75, s * 0.28);
        // トラスのリベット穴・筋交いライン
        p.line(-s * 0.75, -s * 0.12, -s * 0.25, s * 0.12);
        p.line(-s * 0.25, s * 0.12, s * 0.25, -s * 0.12);
        p.line(s * 0.25, -s * 0.12, s * 0.75, s * 0.12);
      } else if (mc.shapeType === 'gear_ring') {
        // 浮遊する破損した歯車リング（houseki_maxres2.jpg 中央右の黒い歯車輪）
        p.noFill();
        p.stroke(isFireMode ? [32, 10, 10, 242] : [16, 20, 28, 242]);
        p.strokeWeight(5.8 * scale);
        p.arc(0, 0, s * 0.82, s * 0.82, 0, p.PI * 1.55);
        for (let k = 0; k < 6; k++) {
          const a = (p.TWO_PI / 8) * k;
          p.line(Math.cos(a) * s * 0.35, Math.sin(a) * s * 0.35, Math.cos(a) * s * 0.5, Math.sin(a) * s * 0.5);
        }
      } else {
        // 3D黒いモノリス立方体（内側に白い光／マグマの亀裂つき — houseki_maxres1.jpg）
        const ox = Math.cos(mc.tilt) * s * 0.38;
        const oy = Math.sin(mc.tilt) * s * 0.38;

        p.noStroke();
        p.fill(isFireMode ? [42, 14, 12, 238] : [26, 34, 46, 232]);
        p.quad(-s * 0.5, -s * 0.5, s * 0.5, -s * 0.5, s * 0.5 + ox, -s * 0.5 + oy, -s * 0.5 + ox, -s * 0.5 + oy);

        p.fill(isFireMode ? [22, 6, 6, 242] : [12, 16, 26, 240]);
        p.quad(s * 0.5, -s * 0.5, s * 0.5 + ox, -s * 0.5 + oy, s * 0.5 + ox, s * 0.5 + oy, s * 0.5, s * 0.5);

        p.fill(isFireMode ? [30, 10, 10, 245] : [18, 24, 36, 242]);
        p.stroke(isFireMode ? [255, 135, 52, 140] : [95, 195, 235, 120]);
        p.strokeWeight(1.0 * scale);
        p.rectMode(p.CENTER);
        p.rect(0, 0, s, s);

        if (mc.hasCoreSlit) {
          const slitPulse = 0.75 + 0.25 * Math.sin(this.time * 3.8 + mc.slitPhase);
          p.stroke(isFireMode ? [255, 175, 65, 245] : [245, 252, 255, 245]);
          p.strokeWeight(3.4 * scale);
          p.line(-s * 0.24, s * 0.2, s * 0.24, -s * 0.2);
          p.stroke(isFireMode ? [255, 68, 24, 135 * slitPulse] : [52, 225, 248, 135 * slitPulse]);
          p.strokeWeight(8.5 * scale);
          p.line(-s * 0.24, s * 0.2, s * 0.24, -s * 0.2);
        }
      }

      p.pop();
    }
  }

  /**
   * 暴走する「青白い結晶の樹脈・神経網」（houseki_sb4.jpg Row 1 Col 4-5）
   */
  drawGlowingCrystalTreeVeins(cx, cy, w, h, scale, glow) {
    const p = this.p;
    p.push();
    p.noFill();

    for (let i = 0; i < this.crystalBranches.length; i++) {
      const br = this.crystalBranches[i];
      const x1 = br.baseX - this.parallaxX * 0.3;
      const y1 = h * 0.94;
      const x4 = br.topX - this.parallaxX * 0.65;
      const y4 = h * (1.0 - br.heightRatio);
      const sway = Math.sin(this.time * 2.1 + br.phase) * 24 * scale;

      const x2 = p.lerp(x1, x4, 0.35) + br.curve + sway * 0.5;
      const y2 = p.lerp(y1, y4, 0.35);
      const x3 = p.lerp(x1, x4, 0.72) - br.curve * 0.6 + sway;
      const y3 = p.lerp(y1, y4, 0.72);

      p.stroke(38, 225, 255, 95 * glow);
      p.strokeWeight(4.2 * scale);
      p.bezier(x1, y1, x2, y2, x3, y3, x4, y4);

      p.stroke(225, 252, 255, 215 * glow);
      p.strokeWeight(1.4 * scale);
      p.bezier(x1, y1, x2, y2, x3, y3, x4, y4);
    }

    p.pop();
  }

  /**
   * 3Dブリリアントカット宝石群と斜め✕字スターバースト光芒の更新・描画
   */
  updateAndDrawBrilliantGems(cx, cy, w, h, scale, dt, speedMult, glow, isFireMode) {
    const p = this.p;

    for (let i = 0; i < this.gems.length; i++) {
      const gm = this.gems[i];
      const vortexVx = Math.cos(this.time * 1.6 + gm.orbitPhase) * 0.65;
      gm.x += (gm.vx + vortexVx) * speedMult * dt;
      gm.y += gm.vy * speedMult * dt;
      gm.rot += gm.rotSpeed * speedMult * dt;
      gm.tilt += gm.tiltSpeed * speedMult * dt;

      if (gm.y < -90 * scale) {
        gm.y = h + 80 * scale;
        gm.x = p.random(w);
      }
      if (gm.x < -90 * scale) gm.x = w + 80 * scale;
      if (gm.x > w + 90 * scale) gm.x = -80 * scale;

      const gx = gm.x - this.parallaxX * gm.depth;
      const gy = gm.y - this.parallaxY * gm.depth;

      this.drawSingleBrilliantGem(
        gx,
        gy,
        gm.size,
        gm.rot,
        gm.tilt,
        gm.hueVariant,
        gm.layer,
        gm.sparklePhase,
        gm.sparkleSpeed,
        scale,
        glow,
        isFireMode
      );
    }
  }

  /**
   * ブリリアントカット宝石1個と頂点の「斜め十字（✕字）スターバースト光芒」描画
   */
  drawSingleBrilliantGem(x, y, size, rot, tilt, hueVariant, layer, sparklePhase, sparkleSpeed, scale, glow, isFireMode) {
    const p = this.p;

    let baseRGB, darkRGB, lightRGB;
    if (hueVariant === 3 || (isFireMode && hueVariant % 2 === 1)) {
      // 紅蓮ルビー・ブラッドクリムゾン
      baseRGB = [245, 38, 68];
      darkRGB = [118, 8, 24];
      lightRGB = [255, 205, 215];
    } else if (hueVariant === 4) {
      // 琥珀アンバーゴールド
      baseRGB = [252, 165, 38];
      darkRGB = [128, 52, 10];
      lightRGB = [255, 242, 195];
    } else if (hueVariant === 0) {
      // フォスフォフィライト・エメラルドティール（houseki_maxres3.jpg の象徴色）
      baseRGB = [22, 218, 195];
      darkRGB = [8, 72, 82];
      lightRGB = [195, 255, 248];
    } else if (hueVariant === 1) {
      // アイスシアン・アクアマリン
      baseRGB = [65, 212, 252];
      darkRGB = [14, 82, 125];
      lightRGB = [225, 252, 255];
    } else {
      // ディープサファイアブルー
      baseRGB = [38, 135, 242];
      darkRGB = [10, 38, 96];
      lightRGB = [205, 238, 255];
    }

    const alphaScale = layer === 2 ? 0.84 : 0.96;
    const wRatio = 0.72 + 0.28 * Math.cos(tilt);
    const crownH = size * 0.28;
    const pavH = size * 0.68;
    const girdleW = size * wRatio;
    const tableW = girdleW * 0.56;

    p.push();
    p.translate(x, y);
    p.rotate(rot);

    // 背後の発光オーラ
    p.noStroke();
    p.fill(baseRGB[0], baseRGB[1], baseRGB[2], 46 * glow * alphaScale);
    p.circle(0, 0, size * 1.75);

    const gLeft = -girdleW * 0.5;
    const gRight = girdleW * 0.5;
    const gMidLeft = -girdleW * 0.18;
    const gMidRight = girdleW * 0.18;
    const culetY = pavH;

    p.stroke(lightRGB[0], lightRGB[1], lightRGB[2], 185 * alphaScale);
    p.strokeWeight(0.95 * scale);

    // パビリオン3面
    p.fill(darkRGB[0], darkRGB[1], darkRGB[2], 225 * alphaScale);
    p.triangle(gLeft, 0, gMidLeft, 0, 0, culetY);

    p.fill(baseRGB[0], baseRGB[1], baseRGB[2], 235 * alphaScale);
    p.triangle(gMidLeft, 0, gMidRight, 0, 0, culetY);

    p.fill(lightRGB[0], lightRGB[1], lightRGB[2], 215 * alphaScale);
    p.triangle(gMidRight, 0, gRight, 0, 0, culetY);

    // クラウン3面
    const tLeft = -tableW * 0.5;
    const tRight = tableW * 0.5;
    const topY = -crownH;

    p.fill(baseRGB[0], baseRGB[1], baseRGB[2], 230 * alphaScale);
    p.quad(gLeft, 0, gMidLeft, 0, tLeft, topY, gLeft * 0.78, topY * 0.65);

    const flash = 0.5 + 0.5 * Math.sin(this.time * sparkleSpeed + sparklePhase);
    p.fill(
      p.lerp(baseRGB[0], 255, 0.5 + 0.45 * flash),
      p.lerp(baseRGB[1], 255, 0.5 + 0.45 * flash),
      p.lerp(baseRGB[2], 255, 0.5 + 0.45 * flash),
      245 * alphaScale
    );
    p.quad(gMidLeft, 0, gMidRight, 0, tRight, topY, tLeft, topY);

    p.fill(darkRGB[0], darkRGB[1], darkRGB[2], 220 * alphaScale);
    p.quad(gMidRight, 0, gRight, 0, gRight * 0.78, topY * 0.65, tRight, topY);

    p.stroke(255, 255, 255, 225 * alphaScale);
    p.strokeWeight(1.2 * scale);
    p.line(gLeft, 0, gRight, 0);

    p.pop();

    // 宝石頂点の斜め十字（✕字）スターバースト光芒
    if (flash > 0.58 || this.flareBoost > 0.25) {
      const flareIntensity = Math.max((flash - 0.58) / 0.42, this.flareBoost * 0.65);
      const flareRadius = size * (1.05 + flareIntensity * 1.1) * glow;
      this.drawDiagonalCrossStarburst(
        x + Math.cos(rot) * gMidLeft * 0.6,
        y + Math.sin(rot) * (-crownH * 0.5),
        flareRadius,
        baseRGB,
        flareIntensity,
        scale
      );
    }
  }

  /**
   * 斜め十字（✕字）スターバースト・レンズフレア描画（houseki_maxres3.jpg）
   */
  drawDiagonalCrossStarburst(x, y, radius, rgb, intensity, scale) {
    const p = this.p;
    p.push();
    p.translate(x, y);
    p.rotate(p.QUARTER_PI);

    p.noStroke();
    p.fill(rgb[0], rgb[1], rgb[2], 110 * intensity);
    p.circle(0, 0, radius * 0.8);

    p.fill(248, 254, 255, 245 * intensity);
    const thick = Math.max(1.6, 3.6 * scale * intensity);
    p.beginShape();
    p.vertex(0, -radius);
    p.vertex(thick, -thick);
    p.vertex(radius, 0);
    p.vertex(thick, thick);
    p.vertex(0, radius);
    p.vertex(-thick, thick);
    p.vertex(-radius, 0);
    p.vertex(-thick, -thick);
    p.endShape(p.CLOSE);

    p.fill(255, 255, 255, 255 * intensity);
    p.circle(0, 0, thick * 2.4);

    p.pop();
  }

  /**
   * 立ち昇る瓦礫チップ・火の粉・結晶スパークの更新と描画
   */
  updateAndDrawEmberSparks(w, h, scale, dt, speedMult, glow) {
    const p = this.p;
    p.push();
    p.noStroke();

    for (let i = 0; i < this.emberSparks.length; i++) {
      const sp = this.emberSparks[i];
      sp.x += (sp.vx + Math.sin(this.time * 3.0 + sp.phase) * 0.8) * speedMult * dt;
      sp.y += sp.vy * speedMult * dt;
      sp.rot += sp.rotSpeed * dt;

      if (sp.y < -20) {
        sp.y = h + 20;
        sp.x = p.random(w);
      }
      if (sp.x < -20) sp.x = w + 20;
      if (sp.x > w + 20) sp.x = -20;

      if (sp.isRubbleChip) {
        // 舞い上がる黒い瓦礫の小破片
        p.push();
        p.translate(sp.x, sp.y);
        p.rotate(sp.rot);
        p.fill(18, 24, 34, sp.alpha * 0.9);
        p.rect(-sp.size * 0.6, -sp.size * 0.4, sp.size * 1.3, sp.size * 0.8);
        p.pop();
      } else {
        if (sp.isFire || this.modeIndex === 1) {
          p.fill(255, 145, 38, sp.alpha * glow);
        } else {
          p.fill(68, 242, 235, sp.alpha * glow);
        }
        p.rect(sp.x, sp.y, sp.size, sp.size);
      }
    }

    p.pop();
  }

  /**
   * デジタル・ピクセルグリッチ＆RGB色収差ティアリング帯（houseki_maxresdefault.jpg）
   */
  drawDigitalGlitchAndChromaticTear(w, h, scale, glow) {
    const p = this.p;
    p.push();
    p.rectMode(p.CENTER);
    p.noStroke();

    for (let i = 0; i < this.glitchBlocks.length; i++) {
      const gb = this.glitchBlocks[i];
      const blink = Math.sin(this.time * gb.blinkSpeed + gb.phase);
      if (blink < 0.05) continue;

      const gx = gb.x - this.parallaxX * gb.depth;
      const gy = gb.y - this.parallaxY * gb.depth;
      const alpha = p.map(blink, 0.05, 1.0, 55, 235) * glow;

      p.fill(gb.col[0], gb.col[1], gb.col[2], alpha);
      p.rect(gx, gy, gb.w, gb.h);
    }

    // 破砕インパクト時に画面を横に走るRGBグリッチ・スキャンライン帯
    if (this.impactShake > 3.0 || p.random() < 0.18) {
      const tearCount = 3;
      for (let t = 0; t < tearCount; t++) {
        const ty = p.random(h * 0.12, h * 0.88);
        const tw = p.random(w * 0.25, w * 0.85);
        const tx = p.random(w * 0.2, w * 0.8);
        p.fill(38, 245, 235, 95 * glow);
        p.rect(tx - 6 * scale, ty, tw, 3.2 * scale);
        p.fill(255, 45, 85, 95 * glow);
        p.rect(tx + 6 * scale, ty + 2 * scale, tw, 2.8 * scale);
      }
    }

    p.pop();
  }

  /**
   * カーソル移動軌跡の結晶シャード＆✕字スパーク描画
   */
  updateAndDrawCursorTrail(scale, dt, glow) {
    if (this.cursorTrail.length === 0) return;

    for (let i = this.cursorTrail.length - 1; i >= 0; i--) {
      const tr = this.cursorTrail[i];
      tr.alpha -= 8.0 * dt;
      tr.rot += 0.08 * dt;
      if (tr.alpha <= 0) {
        this.cursorTrail.splice(i, 1);
        continue;
      }

      const fade = tr.alpha / 255;
      const col =
        tr.hueVariant === 3
          ? [255, 58, 78]
          : tr.hueVariant === 4
          ? [255, 175, 42]
          : [42, 238, 215];
      this.drawDiagonalCrossStarburst(tr.x, tr.y, tr.size * 1.5, col, fade * glow, scale);
    }
  }

  /**
   * MV特有のシネマスコープ上下黒帯（レターボックス）
   */
  drawCinemascopeLetterbox(w, h) {
    const p = this.p;
    const barH = Math.round(h * 0.075);
    p.push();
    p.noStroke();
    p.fill(4, 5, 8, 255);
    p.rect(0, 0, w, barH);
    p.rect(0, h - barH, w, barH);
    p.pop();
  }
}
